import {LitElement, html, nothing, unsafeCSS, type TemplateResult} from 'lit';
import styles from './styles/quiz.css?inline';
import {QuizController} from './quiz-controller';
import {instructions} from './templates/instructions';
import {quizControls} from './templates/quiz-controls';
import {characterDisplay} from './templates/character-display';
import {optionButtons} from './templates/option-buttons';
import {answerFeedback, feedbackText} from './templates/answer-feedback';
import {progressLine} from './templates/progress-line';
import {progressPanel} from './templates/progress-panel';
import {categorySuggestion} from './templates/category-suggestion';
import {goalCard, restingText, sessionRest} from './templates/session-end';
import type {MilestoneKind} from './quiz-controller';
import {STORAGE_KEY} from './progress-store';

const TAG = 'devanagari-quiz';

/** 節目の読み上げ文。チップは 1 語だけなので、スクリーンリーダーにはここで文を与える */
const MILESTONE_TEXT: Record<MilestoneKind, string> = {
  定着: 'この字は定着しました',
  克服: '苦手だった字を克服しました',
  目標達成: '今日の目標に届きました',
};
const FONT_LINK_ID = 'dq-noto-sans-devanagari';
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;700&display=swap';

/**
 * @font-face は shadow root 内では機能しないため、フォントは必ず document 側に登録する。
 * 埋め込み先のページに <link> を 1 本足すことになるので、id で多重注入を防ぐ。
 */
function injectWebfont() {
  if (document.getElementById(FONT_LINK_ID)) return;

  const preconnect = document.createElement('link');
  preconnect.id = `${FONT_LINK_ID}-preconnect`;
  preconnect.rel = 'preconnect';
  preconnect.href = 'https://fonts.gstatic.com';
  preconnect.crossOrigin = '';

  const stylesheet = document.createElement('link');
  stylesheet.id = FONT_LINK_ID;
  stylesheet.rel = 'stylesheet';
  stylesheet.href = FONT_HREF;
  // 埋め込み先が style-src を絞った CSP を持つ場合、読み込みは無言で失敗する。
  // 気づけるようにログだけ出す（システムフォントにフォールバックするので動作は続く）。
  stylesheet.onerror = () => {
    console.warn(`[${TAG}] webfont failed to load; falling back to system Devanagari fonts.`);
  };

  document.head.append(preconnect, stylesheet);
}

/**
 * デーヴァナーガリー文字練習クイズ。
 *
 * 属性:
 *   standalone  … 全画面グラデーション背景と余白を出す。デモページ（iframe 互換）専用。
 *   no-webfont  … Google Fonts の読み込みを止める。
 *
 * ::part(card) / ::part(character) / ::part(option) / ::part(reset) を公開している。
 */
export class DevanagariQuiz extends LitElement {
  static styles = [unsafeCSS(styles)];

  #quiz = new QuizController(this);

  /**
   * 反応時間の計測に関わる DOM の監視はここに集める（コントローラは DOM を知らない）。
   * - タブが隠れた問題は計測しない（戻ってきてから答えた時間に意味が無い）
   * - 隠れるとき・ページを離れるときに保留中の記録を書く。unload は bfcache を壊すので使わない
   */
  #onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      this.#quiz.markUntimed();
      this.#quiz.flushProgress();
    }
  };
  #onPageHide = () => this.#quiz.flushProgress();
  /** 別のタブが記録を書いたら取り込む。key が null なのは clear() で全部消されたとき */
  #onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY || e.key === null) this.#quiz.resyncProgress();
  };
  /** 提案カード・区切りカードにフォーカスを移したか（同じカードで二度移さない） */
  #focusedSuggestion: unknown = null;
  #focusedGoalCard: unknown = null;
  #focusedRest = false;
  /** 直前の描画で提案・区切り・休止のどれかが出ていた */
  #wasInCard = false;

  connectedCallback() {
    // ウェブフォントが届く前に出した問題は、フォント切替の瞬間が反応時間に混ざるので計測しない
    this.#quiz.setTimingBlocked(true);
    super.connectedCallback();
    // 属性を見てから注入する。モジュール評価時に注入すると no-webfont が効かない
    if (!this.hasAttribute('no-webfont')) injectWebfont();
    const fonts = document.fonts;
    if (fonts?.ready) {
      fonts.ready.then(() => this.#quiz.setTimingBlocked(false));
    } else {
      this.#quiz.setTimingBlocked(false);
    }
    document.addEventListener('visibilitychange', this.#onVisibility);
    window.addEventListener('pagehide', this.#onPageHide);
    window.addEventListener('storage', this.#onStorage);
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.#onVisibility);
    window.removeEventListener('pagehide', this.#onPageHide);
    window.removeEventListener('storage', this.#onStorage);
    super.disconnectedCallback();
  }

  /**
   * 提案カードは選択肢ボタンを差し替えて出るので、押していたボタンが消えてフォーカスが
   * body に落ちる。そのときだけカード自体（role=group、tabindex=-1）に移して、グループ名を
   * 読ませる。○ の 1.3 秒の間に利用者が「記録」やチェックボックスへ移していたら奪わない
   * （shadow root の activeElement が残っている）。
   */
  updated() {
    const s = this.#quiz.pendingSuggestion;
    if (s && this.#focusedSuggestion !== s) {
      this.#focusedSuggestion = s;
      if (!this.shadowRoot?.activeElement) {
        this.shadowRoot?.getElementById('dq-suggestion')?.focus();
      }
    }
    if (!s) this.#focusedSuggestion = null;
    // 区切りのカードも同じ。押していた選択肢が消えるので、体が空いていればカードへ
    const g = this.#quiz.goalCard;
    if (g && this.#focusedGoalCard !== g) {
      this.#focusedGoalCard = g;
      if (!this.shadowRoot?.activeElement) {
        this.shadowRoot?.getElementById('dq-goal-card')?.focus();
      }
    }
    if (!g) this.#focusedGoalCard = null;
    // 「今日はここまで」を押すとカードごと消えるので、休止のブロックへ 1 回だけ移す
    const rest = this.#quiz.sessionClosed;
    if (rest && !this.#focusedRest) {
      this.#focusedRest = true;
      if (!this.shadowRoot?.activeElement) {
        this.shadowRoot?.getElementById('dq-session-rest')?.focus();
      }
    }
    if (!rest) this.#focusedRest = false;
    // 提案・区切り・休止のブロックは「続ける」等で丸ごと消えるので、その中にあった
    // フォーカスは body に落ちる。そのときだけ最初の選択肢へ移す（マウス操作では
    // activeElement が残っているので触らない）
    const inCard = !!(s || g || rest);
    if (this.#wasInCard && !inCard && !this.shadowRoot?.activeElement) {
      this.shadowRoot?.querySelector<HTMLElement>('[part="option"]')?.focus();
    }
    this.#wasInCard = inCard;
  }

  /** 記録パネルはトリガーと DOM 上離れているので、開いたら見出しへフォーカスを移す */
  async #toggleProgressPanel() {
    const q = this.#quiz;
    q.toggleProgressPanel();
    if (!q.progressPanelOpen) return;
    await this.updateComplete;
    this.shadowRoot?.getElementById('dq-progress-heading')?.focus();
  }

  /** 消去の確認では、フォーカスを危険側ではなく「やめる」に置く */
  async #requestClear() {
    this.#quiz.requestClearProgress();
    await this.updateComplete;
    this.shadowRoot?.getElementById('dq-clear-cancel')?.focus();
  }

  /** 消去を確定すると押したボタンが消えるので、10 秒だけ出る「元に戻す」に移す */
  async #confirmClear() {
    this.#quiz.confirmClearProgress();
    await this.updateComplete;
    this.shadowRoot?.getElementById('dq-clear-undo')?.focus();
  }

  render(): TemplateResult {
    const q = this.#quiz;
    const summary = q.summary;
    // 何をするかを 1 行で示す。一度も答えたことがない人には最初の字で、
    // 反転した問題では毎回（向きが変わったことを言葉で伝える）。
    // はじめての字の教示（同じ場所）が出ている間は出さない
    const prompt =
      q.showResult || !q.currentCharacter || q.preview
        ? ''
        : q.reversed
          ? 'この読み方の字を選んでください'
          : q.totals.answered === 0
            ? 'この字の読み方を選んでください'
            : '';
    const hud = progressLine({
      ...summary,
      panelOpen: q.progressPanelOpen,
      days: q.totals.days,
      streak: q.streak,
      milestone: q.milestone,
      persistent: q.awaitingContinue,
      // 案内の 1 行と連続正解チップは同じ下端に並ぶ。350px では反転の案内がチップに重なる
      chipRaised: prompt !== '',
      onToggle: () => this.#toggleProgressPanel(),
    });
    const wrongText = `${feedbackText(q.currentCharacter, q.contrastBases, q.lastChoice)}。この字はまた出題されます`;
    const correctText = q.milestone ? `正解。${MILESTONE_TEXT[q.milestone.kind]}` : '正解';

    return html`
      <div class="dq-root max-w-4xl mx-auto">
        <div part="card" class="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div class="p-6 sm:p-8">
            <h1 class="text-2xl md:text-3xl font-bold text-indigo-800 mb-6 text-center">
              <span class="font-devanagari">देवनागरी अक्षर क्विज</span>
              <span class="block text-lg font-medium text-indigo-600 mt-1">
                Devanagari Script Quiz
              </span>
            </h1>

            <!-- 出題を先頭に置く。設定と使用方法が先だと、375×667 の端末では出題文字が
                 y≈816px・選択肢が y≈1104px に沈み、初めて開いた人は 1 画面半スクロールしないと
                 「何をするアプリか」が分からない。戻ってきた学習者も、開いてすぐ答え始められる。
                 縦の間隔は入れ替え前と同じ合計（h1 の mb-6 と mt-8 が 4 つ＝152px。旧配置の
                 使用方法の mb-6 は次の mt-8 と相殺されて消えていた）に保ち、初期描画の高さ＝
                 README のプレースホルダ実測値を変えない（14 幅で実測して差 0 を確認済み）。 -->
            <div>
              <div>
                ${characterDisplay(
                  q.currentCharacter?.char,
                  q.showResult,
                  q.resultCorrect,
                  wrongText,
                  q.awaitingContinue,
                  hud,
                  correctText,
                  prompt,
                  // 下中央の「字 = 読み」。はじめての字は出題直後 PREVIEW_MS の教示として、
                  // 正解の 1.3 秒にはその字の対として。節目のチップと同じ場所なので、
                  // 節目があるときはチップを優先する
                  q.preview && !q.showResult
                    ? {...q.preview, label: 'はじめての字'}
                    : q.showResult && q.resultCorrect && !q.milestone && q.currentCharacter
                      ? {char: q.currentCharacter.char, romanized: q.currentCharacter.romanized}
                      : null,
                  q.sessionClosed ? restingText(q.closedSummary) : nothing,
                  q.reversed,
                  q.currentCharacter?.romanized ?? '',
                  q.preview && !q.showResult
                    ? `はじめての字です。読みは ${q.preview.romanized}`
                    : '',
                  q.firstContact
                )}
                ${q.awaitingContinue
                  ? answerFeedback(q.currentCharacter, q.contrastBases, q.lastChoice, () =>
                      q.continueToNext()
                    )
                  : q.goalCard
                    ? goalCard(
                        q.goalCard,
                        () => q.closeSession(),
                        () => q.continueSession()
                      )
                    : q.sessionClosed
                      ? sessionRest(() => q.continueSession())
                      : q.pendingSuggestion
                        ? categorySuggestion(
                            q.pendingSuggestion,
                            () => q.acceptSuggestion(),
                            () => q.dismissSuggestion()
                          )
                        : optionButtons(
                            q.options,
                            q.locked,
                            (romanized) => q.checkAnswer(romanized),
                            q.reversed
                          )}

                <!-- 記録パネルは「記録」の直下（選択肢のすぐ後）に開く。ボタンの後ろに置くと、
                     押した場所から遠く離れて現れ、開いたことに気づけなかった -->
                ${q.progressPanelOpen
                  ? progressPanel({
                      breakdown: q.categoryBreakdown,
                      weak: q.weakCharacters,
                      totals: q.totals,
                      recentDays: q.recentDays,
                      goal: q.goal,
                      recordingAvailable: q.recordingAvailable,
                      recordingEnabled: q.recordingEnabled,
                      recordingReadOnly: q.recordingReadOnly,
                      confirmingClear: q.confirmingClear,
                      clearedCount: q.clearedCount,
                      clearedPersisted: q.clearedPersisted,
                      reverseEnabled: q.reverseEnabled,
                      onGoal: (g) => q.setGoal(g),
                      onReverse: (on) => q.setReverseEnabled(on),
                      onRecording: (on) => q.setRecordingEnabled(on),
                      onRequestClear: () => this.#requestClear(),
                      onCancelClear: () => q.cancelClearProgress(),
                      onConfirmClear: () => this.#confirmClear(),
                      onUndoClear: () => q.undoClearProgress(),
                    })
                  : nothing}

                <!-- 三次の操作。塗りつぶしにすると選択肢（白＋枠）より強く見え、誤答後は「次へ」と
                     同じ見た目の塗りボタンが 2 つ並ぶ。枠は outline で描き、高さ 48px は変えない
                     （プレースホルダの実測値に影響させない） -->
                <div class="mt-8 text-center">
                  <button
                    type="button"
                    part="reset"
                    @click=${() => q.resetQuiz()}
                    class="px-4 py-3 whitespace-nowrap bg-white text-gray-700 font-medium rounded-lg outline outline-1 -outline-offset-1 outline-gray-500 hover:bg-gray-50 hover:outline-gray-600 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 focus-visible:ring-offset-2"
                  >
                    別の文字にする (New set)
                  </button>
                </div>

              </div>

              <div class="mt-8">
                ${quizControls({
                  vowelsChecked: q.vowelsChecked,
                  consonantsChecked: q.consonantsChecked,
                  dependentVowelsChecked: q.dependentVowelsChecked,
                  viramaChecked: q.viramaChecked,
                  ayogavahaChecked: q.ayogavahaChecked,
                  conjunctsChecked: q.conjunctsChecked,
                  numeralsChecked: q.numeralsChecked,
                  characterCount: q.characterCount,
                  sliderMax: q.sliderMax,
                  hasSelection: q.hasSelection,
                  onToggle: (key, value) => q.setChecked(key, value),
                  onCharacterCountInput: (count) => q.setCharacterCount(count),
                  onCharacterCountCommit: (count) => q.commitCharacterCount(count),
                })}
              </div>

              ${instructions(q.instructionsExpanded, () => q.toggleInstructions())}
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'devanagari-quiz': DevanagariQuiz;
  }
}

// CMS のフリーページは同じ script を二度貼ってしまう事故が起きやすい。
// customElements.define は同名再登録で NotSupportedError を投げるのでガードする。
if (customElements.get(TAG)) {
  console.warn(`[${TAG}] already defined; skipping duplicate registration.`);
} else {
  customElements.define(TAG, DevanagariQuiz);
}
